// Typed HTTP client for the platform admin console (/platform-api), parallel
// to lib/api/client.ts but talking to a structurally separate backend auth
// domain (see backend/middleware/platformAuth.js): no X-Branch-Id, no kiosk
// token fallback, and its own token storage (lib/platformApi/session.ts)
// so a tenant staff session and an operator session never collide.
//
// ApiError/normalizeCode are intentionally duplicated in miniature here
// rather than imported from lib/api/client.ts — that file's error handling
// is entangled with tenant-session concerns (branch headers, kiosk fallback,
// the tenant onSessionExpired hook) that don't apply here, and the two
// response contracts share a shape (utils/apiResponse.js on the backend)
// without needing to share a module on the frontend.
import { getPlatformToken, clearPlatformSession } from './session';

const BASE = '/platform-api';

export type PlatformApiErrorCode =
  | 'validation' | 'login' | 'forbidden' | 'not-found' | 'conflict' | 'exception';

export class PlatformApiError extends Error {
  readonly code: PlatformApiErrorCode;
  readonly status: number;

  constructor(message: string, code: PlatformApiErrorCode, status: number) {
    super(message);
    this.name = 'PlatformApiError';
    this.code = code;
    this.status = status;
  }
}

function normalizeCode(raw: unknown, status: number): PlatformApiErrorCode {
  const known: PlatformApiErrorCode[] = ['validation', 'login', 'forbidden', 'not-found', 'conflict', 'exception'];
  if (typeof raw === 'string' && (known as string[]).includes(raw)) return raw as PlatformApiErrorCode;
  switch (status) {
    case 400: return 'validation';
    case 401: return 'login';
    case 403: return 'forbidden';
    case 404: return 'not-found';
    case 409: return 'conflict';
    default: return 'exception';
  }
}

let onSessionExpired: () => void = () => {
  clearPlatformSession();
  window.location.href = '/?platform';
};

export function setPlatformSessionExpiredHandler(fn: () => void): void {
  onSessionExpired = fn;
}

function buildHeaders(hasBody: boolean): HeadersInit {
  const headers: Record<string, string> = {};
  if (hasBody) headers['Content-Type'] = 'application/json';
  const token = getPlatformToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: buildHeaders(body !== undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    if (res.status === 401 && getPlatformToken() && !path.startsWith('/auth/login')) {
      onSessionExpired();
    }
    const payload = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new PlatformApiError(payload.error || `Request failed (${res.status})`, normalizeCode(payload.code, res.status), res.status);
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined as T;
  return (await res.json()) as T;
}

export const platformApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** Fetches a binary response (an audio note, a screenshot, a backup file)
 * with the operator's Bearer token — an <audio src> or a plain link can't
 * carry the Authorization header, so callers turn this into an object URL. */
export async function fetchPlatformBlob(path: string): Promise<Blob> {
  const res = await fetch(`${BASE}${path}`, { headers: buildHeaders(false) });
  if (!res.ok) {
    if (res.status === 401 && getPlatformToken()) onSessionExpired();
    const payload = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new PlatformApiError(payload.error || `Request failed (${res.status})`, normalizeCode(payload.code, res.status), res.status);
  }
  return res.blob();
}

/** Auth token accessor for callers that need to build a raw fetch (the SSE
 * provisioning stream can't go through `request` above — it needs to read
 * the response body incrementally, not await a single parsed JSON result). */
export function platformAuthHeader(): HeadersInit {
  const token = getPlatformToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
